import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { UserRound } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { t } from '../../i18n/nl.js';
import { fmtDate } from '../../utils/formatUtils.js';
import { toAmsterdamDate, toAmsterdamTime } from '../../utils/dateUtils.js';
import {
  getActiveClientMemberships, primaryMembershipCredits, membershipDisplayName,
  getMembershipCreditBatches, fmtDatumLang, nextCreditGrantDate,
} from '../../utils/membershipUtils.js';

// Volgorde van de saldobalk, gelijk aan het homescherm van de app.
const CREDIT_TYPE_ORDER = { pt: 0, pt_plus: 1, small_group: 2 };

function creditTypeLabel(sessionType) {
  const key = 'credits.creditTypes.' + sessionType;
  const label = t(key);
  return label === key ? t('credits.creditTypes.other') : label;
}

// "1 credit beschikbaar" versus "2 credits beschikbaar". Nul valt in het
// Nederlands onder het meervoud.
function beschikbaarLabel(aantal) {
  return aantal === 1
    ? t('credits.beschikbaarAantalEnkel')
    : t('credits.beschikbaarAantal', { aantal });
}

// De bijschrijvingsregel onder een abonnement: wanneer en hoeveel er bij komt.
// Zonder weekcredits (bv. een los tegoed) valt er niets te melden; met
// weekcredits maar zonder volgende datum is het abonnement opgezegd of
// afgelopen — dan is juist het uitblijven het nieuws.
function toekenningRegel(groep) {
  const perWeek = Number(groep.membership.credits_per_week) || 0;
  if (perWeek <= 0) return null;
  if (!groep.volgendeToekenning) return t('credits.geenToekenningMeer');
  const sleutel = perWeek === 1 ? 'credits.volgendeToekenningEnkel' : 'credits.volgendeToekenning';
  return t(sleutel, { datum: fmtDatumLang(groep.volgendeToekenning), aantal: perWeek });
}

// Eén poppetje = PT, twee = 2-op-1, drie plus een `+` = small group. Puur
// decoratief: het aantal en het abonnementstype staan in de aria-label.
function CreditTypeIcon({ sessionType }) {
  const aantal = sessionType === 'pt' ? 1 : sessionType === 'pt_plus' ? 2 : 3;
  return (
    <span className="credit-summary-icons" aria-hidden="true">
      {Array.from({ length: aantal }, (_, i) => <UserRound key={i} size={14} strokeWidth={2} />)}
      {sessionType === 'small_group' && <span className="credit-summary-plus">+</span>}
    </span>
  );
}

function fmtLesDateTime(isoString) {
  if (!isoString) return '';
  try {
    const d = toAmsterdamDate(isoString);
    const tijd = toAmsterdamTime(isoString);
    return format(d, 'EEE d MMM', { locale: nl }) + ', ' + tijd;
  } catch {
    return '';
  }
}

function fmtCreditReden(tx) {
  const label = t('creditReasons.' + tx.reason) !== 'creditReasons.' + tx.reason
    ? t('creditReasons.' + tx.reason)
    : tx.reason;
  if (tx.group_lessons?.name) {
    const dateStr = tx.group_lessons.starts_at ? ' — ' + fmtLesDateTime(tx.group_lessons.starts_at) : '';
    return label + ' — ' + tx.group_lessons.name + dateStr;
  }
  return label;
}

export default function ClientCreditsPage() {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [history, setHistory] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [membershipData, historyRes] = await Promise.all([
          getActiveClientMemberships(user.id),
          supabase
            .from('credit_transactions')
            .select('id, delta, reason, note, created_at, group_lessons(name, starts_at)')
            .eq('client_id', user.id)
            .lte('created_at', new Date().toISOString())
            .order('created_at', { ascending: false }),
        ]);

        if (historyRes.error) throw historyRes.error;

        const batchData = await getMembershipCreditBatches(
          (membershipData || []).map(m => m.id)
        ).catch(() => []);

        setMemberships(membershipData);
        setHistory(historyRes.data || []);
        setBatches(batchData);
      } catch (err) {
        setError(t('mijnCredits.foutLaden') + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user?.id]);

  // De saldobalk: één post per actief abonnement, in dezelfde volgorde als de
  // app (PT, 2-op-1, small group).
  const saldoItems = memberships
    .map(m => ({
      id: m.id,
      sessionType: m.session_type,
      naam: membershipDisplayName(m, creditTypeLabel(m.session_type)),
      credits: primaryMembershipCredits(m),
    }))
    .sort((a, b) => (CREDIT_TYPE_ORDER[a.sessionType] ?? 99) - (CREDIT_TYPE_ORDER[b.sessionType] ?? 99));

  // Alleen tegoed dat de cliënt nu nog kan gebruiken. Elk actief abonnement
  // krijgt een eigen groep, ook zonder tegoed: dan toont de groep de nul plus
  // de eerstvolgende bijschrijving.
  const actieveBatches = batches.filter(b => (
    b.remaining_credits > 0 && !b.expired_at && new Date(b.expires_at) > new Date()
  ));
  const geldigheidGroepen = memberships.map(m => ({
    membership: m,
    batches: actieveBatches.filter(b => b.client_membership_id === m.id),
    volgendeToekenning: m.credits_per_week > 0 ? nextCreditGrantDate(m) : null,
  }));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('mijnCredits.title')}</h1>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <>
          {/* Huidig saldo — compacte balk zoals het homescherm van de app: per
              abonnement een icoon met het aantal, naast elkaar. De vervaldata
              en abonnementsnamen staan in de geldigheidssectie hieronder. */}
          {saldoItems.length === 0 ? (
            <div className="card" style={{ marginBottom: 24, textAlign: 'center', padding: '32px 24px' }}>
              <p className="text-muted">{t('mijnCredits.geenLidmaatschap')}</p>
            </div>
          ) : (
            <div className="credit-summary">
              <span className="credit-summary-label">{t('credits.saldoTitel')}</span>
              <div className="credit-summary-balances">
                {saldoItems.map(item => (
                  <span
                    key={item.id}
                    className="credit-summary-item"
                    title={item.naam}
                    aria-label={t('credits.saldoToegankelijk', { type: item.naam, aantal: item.credits })}
                  >
                    <CreditTypeIcon sessionType={item.sessionType} />
                    <span className="credit-summary-value">{item.credits}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Geldigheid — cliëntgerichte weergave, gelijk aan de mobiele app.
              Geen batchbronnen of "x van y": dat is interne diagnostiek en
              hoort alleen in het creditbeheerpaneel van de coach. */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
              {t('credits.geldigheidTitel')}
            </h2>
            <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
              {t('credits.geldigheidIntro')}
            </p>
            {geldigheidGroepen.length === 0 ? (
              <p className="text-muted">{t('credits.geenBeschikbaar')}</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {geldigheidGroepen.map(groep => (
                  <div
                    key={groep.membership.id}
                    style={{
                      border: '1px solid var(--color-border)',
                      borderRadius: 10,
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                      {membershipDisplayName(groep.membership, t('mijnAbonnement.onbekend'))}
                    </div>
                    {groep.batches.length > 0 ? (
                      groep.batches.map(b => (
                        <div key={b.id} style={{ marginTop: 8 }}>
                          <div style={{ fontSize: 14, fontWeight: 600 }}>
                            {beschikbaarLabel(b.remaining_credits)}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                            {t('credits.geldigTot', { datum: fmtDatumLang(b.expires_at) })}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600 }}>
                        {beschikbaarLabel(0)}
                      </div>
                    )}
                    {/* Wanneer en hoeveel er bij komt — ook als er nog tegoed
                        staat, want dat is precies wat je hier wilt weten. */}
                    {toekenningRegel(groep) && (
                      <div
                        style={{
                          marginTop: 10,
                          paddingTop: 8,
                          borderTop: '1px solid var(--color-border)',
                          fontSize: 12,
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        {toekenningRegel(groep)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Creditgeschiedenis */}
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>
              {t('mijnCredits.geschiedenisTitel')}
            </h2>
            {history.length === 0 ? (
              <p className="text-muted">{t('mijnCredits.geenGeschiedenis')}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t('mijnCredits.colDatum')}</th>
                      <th>{t('mijnCredits.colReden')}</th>
                      <th style={{ textAlign: 'right' }}>{t('mijnCredits.colAantal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(tx => (
                      <tr key={tx.id}>
                        <td className="text-muted">{fmtDate(tx.created_at)}</td>
                        <td>
                          <div>{fmtCreditReden(tx)}</div>
                          {tx.note && (
                            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{tx.note}</div>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{
                            fontWeight: 700,
                            color: tx.delta > 0 ? 'var(--color-primary)' : 'var(--color-danger)',
                          }}>
                            {tx.delta > 0 ? '+' : ''}{tx.delta}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
